import ShoppingCartCheckoutIcon from '@mui/icons-material/ShoppingCartCheckout'
import { Box, Button, CircularProgress, Skeleton, Stack, Typography, alpha } from '@mui/material'

import type React from 'react'

import { DisabledReasonTooltip } from './disabled-reason-tooltip'

interface ReorderBasketFooterProps {
  grandTotal: string
  taxAcronym: string
  onCheckout: () => void
  checkoutDisabled?: boolean
  checkoutDisabledReason?: string
  checkoutLabel?: string
  loading?: boolean
}

export const ReorderBasketFooter: React.FC<ReorderBasketFooterProps> = ({
  grandTotal,
  taxAcronym,
  onCheckout,
  checkoutDisabled = false,
  checkoutDisabledReason,
  checkoutLabel = 'Checkout',
  loading = false,
}) => {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={1.25}
      sx={{ padding: '12px 12px 14px', containerType: 'inline-size' }}>
      <DisabledReasonTooltip reason={checkoutDisabledReason}>
        <Button
          variant="contained"
          onClick={onCheckout}
          disabled={checkoutDisabled || loading}
          aria-label={checkoutLabel}
          startIcon={
            loading ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <ShoppingCartCheckoutIcon sx={{ fontSize: 15 }} />
            )
          }
          sx={{
            borderRadius: '100px',
            padding: '10px 20px',
            fontSize: 13,
            fontWeight: 700,
            textTransform: 'none',
            letterSpacing: '.1px',
            boxShadow: (theme) => `0 2px 8px ${alpha(theme.palette.primary.main, 0.4)}`,
            '&:hover': {
              boxShadow: (theme) => `0 4px 14px ${alpha(theme.palette.primary.main, 0.45)}`,
              transform: 'translateY(-1px)',
            },
            '@container (max-width: 210px)': {
              '& .MuiButton-startIcon': { margin: 0 },
            },
          }}>
          <Box component="span" sx={{ '@container (max-width: 210px)': { display: 'none' } }}>
            {checkoutLabel}
          </Box>
        </Button>
      </DisabledReasonTooltip>
      <Box sx={{ textAlign: 'right' }}>
        <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
          Total{' '}
          <Box component="span" sx={{ fontSize: 10 }}>
            (inc {taxAcronym})
          </Box>
        </Typography>
        {loading ? (
          <Skeleton variant="text" width={80} sx={{ ml: 'auto' }} />
        ) : (
          <Typography
            sx={{
              fontSize: 19,
              fontWeight: 700,
              lineHeight: 1.1,
              letterSpacing: '-0.3px',
            }}>
            {grandTotal}
          </Typography>
        )}
      </Box>
    </Stack>
  )
}
